import { memo, useLayoutEffect, useRef, type ChangeEvent, type KeyboardEvent } from 'react';

interface PromptTextareaProps {
  value: string;
  placeholder: string;
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}

export const PromptTextarea = memo<PromptTextareaProps>(
  ({ value, placeholder, disabled = false, onChange, onKeyDown }) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const MIN_HEIGHT_PX = 120;
    const MAX_HEIGHT_PX = 480;

    useLayoutEffect(() => {
      const textarea = textareaRef.current;

      if (!textarea) {
        return;
      }

      textarea.style.height = 'auto';

      const scrollHeight = textarea.scrollHeight;
      const nextHeight = Math.max(MIN_HEIGHT_PX, Math.min(scrollHeight, MAX_HEIGHT_PX));

      textarea.style.height = `${nextHeight}px`;
      textarea.style.overflowY = scrollHeight > MAX_HEIGHT_PX ? 'auto' : 'hidden';
    }, [value]);

    return (
      <textarea
        ref={textareaRef}
        className="text-sm/6 p-2.5 bg-bolt-ds-surfaceOne w-full rounded-md border border-bolt-elements-borderColor outline-bolt-elements-item-outlineActive resize-none disabled:cursor-not-allowed placeholder-bolt-elements-textTertiary"
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onKeyDown={onKeyDown}
        onChange={onChange}
        style={{ minHeight: `${MIN_HEIGHT_PX}px`, maxHeight: `${MAX_HEIGHT_PX}px` }}
      />
    );
  },
);

PromptTextarea.displayName = 'PromptTextarea';
